// Mapeia códigos de erro do backend → chaves i18n
// Centraliza pra cada tela tratar do mesmo jeito.

import { ApiError } from './api'

// Aceita params de interpolação (ex: {{code}}) — o `t` do i18next suporta.
// Overload (não param opcional único) pra casar com a assinatura do TFunction
// do i18next sob exactOptionalPropertyTypes.
interface Translator {
  (key: string): string
  (key: string, params: Record<string, unknown>): string
}

export function errorToMessage(err: unknown, t: Translator): string {
  // Erros do backend já tipados
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'provider_not_found':
        return t('errors.providerNotFound')
      // O servidor Xtream manual informado pertence a um provedor cadastrado:
      // orienta a usar o código de acesso (pedir a quem vendeu). NÃO mostramos o
      // código detectado: a detecção acha o código do PROVEDOR dono da URL, mas
      // quem vendeu o acesso pode ser um revendedor com código próprio (diferente).
      case 'endpoint_belongs_to_provider':
      case 'endpoint_already_registered':
        return t('errors.endpointBelongsToProvider')
      // Guard de host da fonte manual / login direto (#555). O backend devolve 422
      // com o CÓDIGO; a redação vive aqui, nos 3 locales (o cliente NUNCA lê o
      // `message` do corpo, só o `error`). Os dois casos ficam separados de
      // propósito: mandar procurar rede interna quando foi o DNS que não respondeu
      // faz o usuário caçar o problema errado.
      case 'blocked_host':
        return t('errors.blockedHost')
      case 'unresolvable_host':
        return t('errors.unresolvableHost')
      // URL malformada ou protocolo != http/https na fonte manual. Já existia no
      // caminho da conta Swipe (configurar fonte); #555 estendeu a validação ao
      // /v1/auth/direct (login direto na TV, digitando no controle remoto) — hoje
      // o maior volume dos dois casos deste PR.
      case 'invalid_server_url':
        return t('errors.invalidServerUrl')
      // O nome dado à lista já está em uso por outra fonte/servidor e não dá pra
      // reaproveitar o registro. Ação do usuário: escolher outro nome.
      case 'source_conflict':
        return t('errors.sourceConflict')
      // 'invalid_xtream_credentials' vem do configurar fonte (PUT /swipe-account/
      // preference) quando o usuário/senha do painel do provedor estão errados.
      // Mesma mensagem de credencial inválida do login.
      case 'invalid_credentials':
      case 'invalid_xtream_credentials':
        return t('errors.invalidCredentials')
      // 'xtream_account_expired' (configurar fonte): a conta DO USUÁRIO no
      // painel do provedor expirou. Mesma orientação de conta expirada.
      case 'account_expired':
      case 'xtream_account_expired':
        return t('errors.accountExpired')
      // Configurar fonte: o provedor existe mas está temporariamente inativo
      // (sem crédito/suspenso). Não é credencial errada; orienta a tentar
      // depois / falar com o provedor.
      case 'provider_inactive':
        return t('errors.providerInactive')
      case 'account_banned':
        return t('errors.accountBanned')
      case 'no_provider':
        // Conta Swipe válida mas sem fonte ativa configurada (I3+I4). NÃO é
        // sessão expirada — conduz o user a cadastrar/validar a fonte.
        return t('errors.noProvider')
      case 'upstream_error':
        return t('errors.upstreamError')
      // Provedor fora/rate-limitado (503 provider_unavailable, api.md §7.4.0).
      // Superfícies fora do ao vivo (VOD/série/catchup/configurar fonte) caem
      // aqui — o player do ao vivo tem seu próprio fluxo calmo de back-off.
      case 'provider_unavailable':
        return t('errors.providerUnavailable')
      case 'invalid_refresh_token':
      case 'invalid_token':
      case 'missing_token':
      case 'unauthorized':
        return t('errors.sessionExpired')
      case 'timeout':
        // Request abortado pelo AbortController (>15s default).
        return t('errors.timeout')
      default:
        // Códigos http_5xx (resposta sem body JSON ou erro infraestrutura)
        // → mensagem de servidor. http_4xx genérico → unknown.
        if (err.code.startsWith('http_5')) return t('errors.serverError')
        return t('errors.unknown')
    }
  }
  // Falha de rede (fetch jogou TypeError em Chromium; DOMException em alguns
  // browsers Firefox/Safari quando offline). Cobre ambos.
  if (
    err instanceof TypeError ||
    (err instanceof DOMException && err.name === 'NetworkError')
  ) {
    return t('errors.networkError')
  }
  return t('errors.unknown')
}
